import { useMemo, useState } from 'react';
import { cx } from 'class-variance-authority';
import * as v from 'valibot';

import { Button, type ColumnDef, DataTable, Pagination, Spinner, type TableAction, Tag, Text } from '@nvision/ui';

import { formatDate } from '@/utils/date';

import { useContractDocuments } from '../../../hooks';
import {
  type ContractDocumentItem,
  type ContractDocumentsParams,
  contractDocumentsParamsSchema,
} from '../../../models';

import styles from './contract-documents.module.css';

interface ContractDocumentsProps {
  contractKey?: string;
  primaryDocumentKey?: string;
}

const DATE_FORMAT = 'dd-MM-yyyy';
const EMPTY_CELL = '—';

const getColumns = (primaryDocumentKey?: string): ColumnDef<ContractDocumentItem>[] => [
  {
    accessorKey: 'name',
    cell: (props) => {
      const { id, name } = props.row.original;

      return (
        <div className={styles.nameCell}>
          <span className={styles.fileName}>{name}</span>
          {id === primaryDocumentKey ? (
            <Tag brand size="sm">
              Primary document
            </Tag>
          ) : null}
        </div>
      );
    },
    header: 'Name',
    size: 280,
  },
  {
    accessorKey: 'type',
    header: 'Type',
    size: 180,
  },
  {
    accessorKey: 'comment',
    header: 'Comments',
    size: 280,
  },
  {
    accessorKey: 'createdOn',
    cell: (props) => formatDate(props.row.original.createdOn, DATE_FORMAT) || EMPTY_CELL,
    header: 'Uploaded On',
    size: 140,
  },
];

const getRowActions = (primaryDocumentKey?: string): TableAction<ContractDocumentItem>[] => [
  {
    align: 'left',
    id: 'edit',
    label: 'Edit',
    onClick: () => undefined,
    variant: 'secondary',
  },
  {
    align: 'left',
    id: 'download',
    label: 'Download',
    onClick: () => undefined,
    variant: 'secondary',
  },
  {
    align: 'left',
    id: 'set-primary',
    isDisabled: (row) => row.id === primaryDocumentKey,
    label: 'Set Primary',
    onClick: () => undefined,
    variant: 'secondary',
  },
  {
    align: 'left',
    id: 'delete',
    label: 'Delete',
    onClick: () => undefined,
    variant: 'destructive',
  },
];

export const ContractDocuments: React.FC<ContractDocumentsProps> = ({ contractKey, primaryDocumentKey }) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [params, setParams] = useState<ContractDocumentsParams>({
    ...v.getDefaults(contractDocumentsParamsSchema),
    contractKey: contractKey ?? '',
  });

  const { data: documents, isLoading } = useContractDocuments(params);
  const columns = useMemo(() => getColumns(primaryDocumentKey), [primaryDocumentKey]);
  const rowActions = useMemo(() => getRowActions(primaryDocumentKey), [primaryDocumentKey]);
  const hasDocuments = Boolean(documents?.totalCount);

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <Text size="sm" weight="semibold">
          Documents
        </Text>
        <div className={styles.actions}>
          {contractKey && hasDocuments && (
            <Button iconLeft="trash" secondary outline isDisabled={selectedItems.length === 0}>
              Delete
            </Button>
          )}
          <Button iconLeft="circle-plus" primary>
            Add File
          </Button>
        </div>
      </div>
      {contractKey && isLoading ? (
        <div className={cx(styles.tableContainer, styles.loader)}>
          <Spinner size="lg" />
        </div>
      ) : contractKey && hasDocuments ? (
        <>
          <div className={styles.tableContainer}>
            <DataTable
              data={documents?.items || []}
              columns={columns}
              page={params.offset / params.limit + 1}
              limit={params.limit}
              totalItems={documents?.totalCount}
              selectedItems={selectedItems}
              onSelectionChange={setSelectedItems}
              actions={rowActions}
              actionsConfig={{ displayMode: 'popover', header: '', size: 70 }}
            />
          </div>
          <Pagination
            page={params.offset / params.limit + 1}
            limit={params.limit}
            totalItems={documents?.totalCount || 0}
            onPageChange={(page: number) => {
              setParams((prev) => ({ ...prev, offset: (page - 1) * params.limit }));
            }}
            onOffsetChange={(offset: number) => setParams((prev) => ({ ...prev, offset }))}
            onLimitChange={(limit: number) => setParams((prev) => ({ ...prev, limit, offset: 0 }))}
          />
        </>
      ) : (
        <Text className={styles.emptyState} size="sm">
          No documents uploaded yet. Click the &apos;Add File&apos; button to upload a file.
        </Text>
      )}
    </section>
  );
};
